import type { AIResponse, VehicleInfo, AIDiagnosisCause, AIFixSteps, ChatSession } from '../types';

// Ollama configuration
const OLLAMA_BASE_URL = process.env.REACT_APP_OLLAMA_URL || 'http://localhost:11434';
const OLLAMA_MODEL = process.env.REACT_APP_OLLAMA_MODEL || 'gemma3:4b';

interface OllamaChatResponse {
  model: string;
  created_at: string;
  message: {
    role: string;
    content: string;
  };
  done: boolean;
}

interface OllamaChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

interface OllamaChatRequest {
  model: string;
  messages: OllamaChatMessage[];
  stream: boolean;
  format?: string;
  options?: {
    temperature?: number;
    top_p?: number;
    max_tokens?: number;
    repeat_penalty?: number;
  };
}

const SYSTEM_INSTRUCTION = `You are an automotive diagnostic AI. Follow these rules STRICTLY:

1. **MULTIPLE CHOICE QUESTIONS:** Ask questions with 3-4 clear, distinct answer options. Examples:
   - "What type of noise does the engine make when starting?" with options: ["Clicking sound", "Grinding noise", "No unusual noise", "Whining sound"] ✓
   - "When does the problem occur?" with options: ["Only when cold", "Only when hot", "All the time", "Only under load"] ✓
   - "What color is the smoke?" with options: ["White smoke", "Black smoke", "Blue smoke", "No visible smoke"] ✓

2. **ONE QUESTION AT A TIME:** Ask only ONE question per response. Do not ask multiple questions.

3. **PROGRESSIVE DIAGNOSIS:** After 4-6 questions, provide a diagnosis. Do not ask the same question twice.

4. **CONVERSATION FLOW:** 
   - First question: Ask about the most obvious symptom with multiple choice options
   - Follow-up questions: Based on the user's previous answers, ask more specific questions
   - Final step: Provide diagnosis with 2-3 most likely causes

5. **HANDLE OFF-TOPIC RESPONSES:** If a user provides information that is not related to vehicle diagnostics, ask them to focus on vehicle symptoms and repeat your previous question.

6. **CRITICAL - JSON OUTPUT ONLY:** Your response must be ONLY a valid JSON object. No explanations, no additional text.

Response Schema:
{
  "type": "question" | "diagnosis",
  "questionText": "string (only if type is question)",
  "options": ["option1", "option2", "option3", "option4"] (only if type is question, 3-4 options),
  "causes": [
    {
      "cause": "string",
      "likelihood": "Most Likely" | "Likely",
      "reason": "string"
    }
  ] (only if type is diagnosis)
}

Examples:
{"type": "question", "questionText": "What type of noise does the engine make when starting?", "options": ["Clicking sound", "Grinding noise", "No unusual noise", "Whining sound"]}
{"type": "diagnosis", "causes": [{"cause": "Faulty starter motor", "likelihood": "Most Likely", "reason": "Engine cranks but won't start"}]}`;

// Removed unused FIX_STEPS_SCHEMA

const cleanResponse = (text: string): string => {
  // Remove common AI thinking patterns
  let cleaned = text
    .replace(/^(Let me|I'll|I can|Based on|Looking at|I see that|I notice that|From the|Given that|It appears that|It seems that|I think|I believe|I would say|In my opinion|To answer your question|To explain|To clarify|To provide|Here's|Here is)/i, '')
    .replace(/^(The|This|A|An)\s+/i, (match) => match.toLowerCase())
    .replace(/\s+/g, ' ')
    .trim();
  
  // If the response starts with a lowercase letter, capitalize it
  if (cleaned && /^[a-z]/.test(cleaned)) {
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }
  
  return cleaned;
};

const parseJsonResponse = <T,>(text: string): T => {
  try {
    // Clean up the response text
    let cleanedText = text.trim();
    
    // Remove markdown code blocks if present
    cleanedText = cleanedText.replace(/^```json\s*|```\s*$/g, '').trim();
    
    // Remove any text before the first { and after the last }
    const firstBrace = cleanedText.indexOf('{');
    const lastBrace = cleanedText.lastIndexOf('}');
    
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      cleanedText = cleanedText.substring(firstBrace, lastBrace + 1);
    }
    
    // Try to parse the JSON
    const parsed = JSON.parse(cleanedText);
    
    // Validate that it has the expected structure for AI responses
    if (typeof parsed === 'object' && parsed !== null) {
      return parsed as T;
    }
    
    throw new Error("Invalid JSON structure");
  } catch (error) {
    console.error("Failed to parse JSON response:", text);
    console.error("Parse error:", error);
    
    // If JSON parsing fails, try to create a fallback response
    // Attempting to create fallback response
    
    // For question type responses, try to extract a question
    if (text.toLowerCase().includes('question') || text.includes('?')) {
      const questionMatch = text.match(/([^.!?]*\?)/);
      if (questionMatch) {
        let question = questionMatch[1].trim();
        return {
          type: 'question',
          questionText: question,
          options: ["Option 1", "Option 2", "Option 3", "Option 4"]
        } as T;
      }
    }
    
    // For diagnosis type responses, try to extract causes
    if (text.toLowerCase().includes('diagnosis') || text.toLowerCase().includes('cause')) {
      return {
        type: 'diagnosis',
        causes: [
          {
            cause: "Unable to parse specific cause from response",
            likelihood: "Most Likely" as const,
            reason: "The AI response could not be properly parsed. Please try again or provide more specific information."
          }
        ]
      } as T;
    }
    
    // Default fallback - provide a generic multiple choice question
    return {
      type: 'question',
      questionText: "When does the problem occur?",
      options: ["Every time I use the vehicle", "Only sometimes", "Only when the engine is cold", "Only when the engine is hot"]
    } as T;
  }
};

const callOllama = async (prompt: string, systemPrompt?: string, options?: { temperature?: number; max_tokens?: number }): Promise<string> => {
  const messages: OllamaChatMessage[] = [];
  
  if (systemPrompt) {
    messages.push({ role: 'system', content: systemPrompt });
  }
  
  messages.push({ role: 'user', content: prompt });

  const requestBody: OllamaChatRequest = {
    model: OLLAMA_MODEL,
    messages,
    stream: false,
    format: 'json',
    options: {
      temperature: options?.temperature || 0.1,
      max_tokens: options?.max_tokens || 1024,
      top_p: 0.9,
      repeat_penalty: 1.1,
    }
  };

  try {
    const response = await fetch(`${OLLAMA_BASE_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Ollama API error: ${response.status} ${response.statusText}`);
    }

    const data: OllamaChatResponse = await response.json();
    return data.message.content;
  } catch (error) {
    console.error('Ollama API call failed:', error);
    console.error('Ollama URL:', OLLAMA_BASE_URL);
    console.error('Ollama Model:', OLLAMA_MODEL);
    throw new Error('Failed to communicate with Ollama. Please ensure Ollama is running locally.');
  }
};

const callOllamaWithImage = async (prompt: string): Promise<string> => {
  // Note: Ollama's vision capabilities depend on the model. For now, we'll use text-only
  // and provide instructions for the user to describe the image
  const enhancedPrompt = `${prompt}

Note: If you need to analyze an image, please ask the user to describe what they see in the image, as this model processes text only.

IMPORTANT: Return only plain text explanation. Do not use JSON format or any code blocks.`;

  // Create a custom request that doesn't use JSON format
  const messages = [
    { role: 'user' as const, content: enhancedPrompt }
  ];

  const requestBody = {
    model: OLLAMA_MODEL,
    messages,
    stream: false,
    // Don't specify format: 'json' to get plain text response
    options: {
      temperature: 0.2,
      max_tokens: 200,
      top_p: 0.9,
      repeat_penalty: 1.1,
    }
  };

  try {
    const response = await fetch(`${OLLAMA_BASE_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Ollama API error: ${response.status} ${response.statusText}`);
    }

    const data: OllamaChatResponse = await response.json();
    return cleanResponse(data.message.content.trim());
  } catch (error) {
    console.error('Ollama API call failed:', error);
    throw new Error('Failed to analyze image. Please try again.');
  }
};

export const analyzePartImagesForFault = async (fileContent: string, mimeType: string): Promise<string> => {
  if (!mimeType.startsWith('image/')) {
    throw new Error('Unsupported file type. Please upload an image.');
  }

  const prompt = `Analyze the provided image of a vehicle part. Describe any visible damage, wear, or anomalies in a single, concise sentence that could be related to a vehicle fault. This description will be used to pre-fill a fault description form for a technician. Examples: "Cracked serpentine belt.", "Visible corrosion on the battery terminal."

Since I cannot process images directly, please ask the user to describe what they see in the image, and I will help them identify potential issues based on their description.`;

  return callOllamaWithImage(prompt);
};

export const startChatSession = async (vehicleInfo: VehicleInfo): Promise<{ chat: ChatSession; response: AIResponse }> => {
  const textPrompt = `Fault: ${vehicleInfo.faultDescription}. Make: ${vehicleInfo.make}, Model: ${vehicleInfo.model}, Year: ${vehicleInfo.year}. DTCs: ${vehicleInfo.dtcCodes || 'None'}.

${vehicleInfo.files && vehicleInfo.files.length > 0 ? 'The user has uploaded images related to the fault. Please ask them to describe what they see in the images if needed for diagnosis.' : ''}

Start the diagnostic process by asking your first multiple choice question about the most obvious symptom.

CRITICAL: Return ONLY a valid JSON object. No explanations, no additional text. Start with { and end with }.

Example response:
{"type": "question", "questionText": "What type of noise does the engine make when starting?", "options": ["Clicking sound", "Grinding noise", "No unusual noise", "Whining sound"]}`;

  try {
    const response = await callOllama(textPrompt, SYSTEM_INSTRUCTION, { temperature: 0.1, max_tokens: 1024 });
    const parsedResponse = parseJsonResponse<AIResponse>(response);

    // Create a chat object that tracks conversation history
    const conversationHistory: Array<{role: 'user' | 'assistant', content: string}> = [
      { role: 'user', content: textPrompt },
      { role: 'assistant', content: response }
    ];

    const chat = {
      sendMessage: async (message: { message: string }) => {
        // Add user message to history
        conversationHistory.push({ role: 'user', content: message.message });
        
        // Create context-aware prompt
        const contextPrompt = `Previous conversation:
${conversationHistory.map(msg => `${msg.role}: ${msg.content}`).join('\n')}

Based on the conversation above, ask the next multiple choice question or provide a diagnosis if you have enough information (after 4-6 questions).

CRITICAL: Return ONLY a valid JSON object. No explanations, no additional text. Start with { and end with }.`;

        const response = await callOllama(contextPrompt, SYSTEM_INSTRUCTION, { temperature: 0.1, max_tokens: 1024 });
        
        // Add AI response to history
        conversationHistory.push({ role: 'assistant', content: response });
        
        return { text: response };
      }
    };

    return { chat, response: parsedResponse };
  } catch (error) {
    console.error('Error starting chat session:', error);
    throw new Error('Failed to start diagnostic session. Please ensure Ollama is running and try again.');
  }
};

export const continueChatSession = async (chat: ChatSession, message: string): Promise<AIResponse> => {
  try {
    const result = await chat.sendMessage({ message });
    const response = parseJsonResponse<AIResponse>(result.text);
    return response;
  } catch (error) {
    console.error('Error continuing chat session:', error);
    throw new Error('Failed to process your response. Please try again.');
  }
};

export const getFixSteps = async (vehicleInfo: VehicleInfo, cause: AIDiagnosisCause): Promise<AIFixSteps> => {
  const prompt = `Vehicle: ${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}.
Fault Description: ${vehicleInfo.faultDescription}.
Diagnosed potential cause: "${cause.cause}".
Reasoning: "${cause.reason}".

Provide a clear, step-by-step guide for an automotive technician to diagnose and repair this specific issue. Focus only on the provided cause. The steps should be actionable and professional.

CRITICAL: Return ONLY a valid JSON object. No explanations, no additional text. Start with { and end with }.

Required JSON format:
{
  "type": "fix",
  "steps": ["Step 1 description", "Step 2 description", "Step 3 description"]
}

Example:
{"type": "fix", "steps": ["Disconnect the battery", "Remove the starter motor", "Test the starter motor", "Replace if faulty", "Reconnect the battery"]}`;

  const systemPrompt = 'You are an expert automotive repair AI. You provide concise, professional repair procedures. Return ONLY valid JSON in the exact format requested. No additional text or explanations.';

  const response = await callOllama(prompt, systemPrompt, { temperature: 0.2, max_tokens: 1024 });
  return parseJsonResponse<AIFixSteps>(response);
};

export const getCauseDetails = async (vehicleInfo: VehicleInfo, cause: AIDiagnosisCause): Promise<string> => {
  const prompt = `For a ${vehicleInfo.year} ${vehicleInfo.make} ${vehicleInfo.model}, provide a detailed explanation for the potential fault cause: "${cause.cause}".

The original fault description was: "${vehicleInfo.faultDescription}".

Please explain:
1. What this component/system is and what it does.
2. Common symptoms when it fails, beyond the one described.
3. Basic diagnostic steps a technician can take to confirm this is the root cause.

Keep the explanation professional, clear, and concise for an automotive technician. Limit the response to around 150 words.

CRITICAL INSTRUCTIONS:
- Return ONLY the final explanation text
- Do NOT include any thinking, reasoning, or "Let me explain" phrases
- Do NOT use JSON format or any code blocks
- Do NOT include phrases like "Based on the information" or "I can see that"
- Start directly with the explanation content
- Write as if you are a technical manual, not a conversational AI`;

  // Create a custom request that doesn't use JSON format
  const messages = [
    { role: 'user' as const, content: prompt }
  ];

  const requestBody = {
    model: OLLAMA_MODEL,
    messages,
    stream: false,
    // Don't specify format: 'json' to get plain text response
    options: {
      temperature: 0.2,
      max_tokens: 300,
      top_p: 0.9,
      repeat_penalty: 1.1,
    }
  };

  try {
    const response = await fetch(`${OLLAMA_BASE_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Ollama API error: ${response.status} ${response.statusText}`);
    }

    const data: OllamaChatResponse = await response.json();
    return cleanResponse(data.message.content.trim());
  } catch (error) {
    console.error('Ollama API call failed:', error);
    throw new Error('Failed to get cause details. Please try again.');
  }
};

export const lookupDtcCode = async (code: string): Promise<string> => {
  const prompt = `Provide a concise, professional explanation for the automotive Diagnostic Trouble Code (DTC): ${code}.
Explain what the code means and list the most common causes. Keep the explanation clear and easy for a technician to understand quickly. Limit the response to 100 words.

IMPORTANT: Return only plain text explanation. Do not use JSON format or any code blocks.`;

  // Create a custom request that doesn't use JSON format
  const messages = [
    { role: 'user' as const, content: prompt }
  ];

  const requestBody = {
    model: OLLAMA_MODEL,
    messages,
    stream: false,
    // Don't specify format: 'json' to get plain text response
    options: {
      temperature: 0.1,
      max_tokens: 200,
      top_p: 0.9,
      repeat_penalty: 1.1,
    }
  };

  try {
    const response = await fetch(`${OLLAMA_BASE_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Ollama API error: ${response.status} ${response.statusText}`);
    }

    const data: OllamaChatResponse = await response.json();
    return cleanResponse(data.message.content.trim());
  } catch (error) {
    console.error('Ollama API call failed:', error);
    throw new Error('Failed to lookup DTC code. Please try again.');
  }
};
